import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { useProjects } from "../../context/ProjectContext";
import {
  Plus, PencilSimple, LightningSlash, Trash, ArrowCounterClockwise,
  ClockClockwise, ArrowRight, User as UserIcon,
} from "@phosphor-icons/react";

const ACTION_META = {
  create: { label: "Created", icon: Plus, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  update: { label: "Updated", icon: PencilSimple, color: "text-sky-700 bg-sky-50 border-sky-200" },
  patch: { label: "Inline", icon: LightningSlash, color: "text-violet-700 bg-violet-50 border-violet-200" },
  delete: { label: "Deleted", icon: Trash, color: "text-red-700 bg-red-50 border-red-200" },
  restore: { label: "Restored", icon: ArrowCounterClockwise, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};
const ACTION_ORDER = ["create", "update", "patch", "delete", "restore"];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function TodayCard({ refreshKey }) {
  const [data, setData] = useState(null);
  const { projectId } = useProjects();

  const load = useCallback(async () => {
    if (!projectId) { setData({ total: 0, by_action: {}, top_actors: [], recent: [], unique_actors: 0, unique_tasks: 0 }); return; }
    try {
      const { data: d } = await api.get("/audit/summary", { params: { project_id: projectId } });
      setData(d);
    } catch {
      setData({ total: 0, by_action: {}, top_actors: [], recent: [], unique_actors: 0, unique_tasks: 0 });
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data) {
    return (
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-5 h-[180px] flex items-center justify-center text-sm text-neutral-400">
        Loading today&apos;s activity…
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-lg p-5 flex flex-col md:flex-row gap-6"
      data-testid="today-card"
    >
      {/* Left: totals + action mix */}
      <div className="flex-1 min-w-[220px] border-b md:border-b-0 md:border-r border-[#E5E5E5] pb-4 md:pb-0 md:pr-6">
        <div className="flex items-center gap-2 mb-3">
          <ClockClockwise size={14} weight="bold" className="text-neutral-500" />
          <p
            className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            Today&apos;s activity
          </p>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span
            className="text-4xl font-black tracking-tight text-[#0A0A0A]"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="today-total"
          >
            {data.total}
          </span>
          <span className="text-sm text-neutral-500">
            change{data.total === 1 ? "" : "s"} by{" "}
            <span className="text-neutral-900 font-mono tabular-nums">{data.unique_actors}</span>{" "}
            user{data.unique_actors === 1 ? "" : "s"} on{" "}
            <span className="text-neutral-900 font-mono tabular-nums">{data.unique_tasks}</span>{" "}
            task{data.unique_tasks === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="today-action-mix">
          {ACTION_ORDER.filter((a) => (data.by_action || {})[a]).map((a) => {
            const meta = ACTION_META[a];
            const Icon = meta.icon;
            return (
              <span
                key={a}
                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.color}`}
                data-testid={`today-action-${a}`}
              >
                <Icon size={10} weight="bold" />
                {meta.label} · {data.by_action[a]}
              </span>
            );
          })}
          {data.total === 0 ? (
            <span className="text-xs text-neutral-400 italic">No activity yet today.</span>
          ) : null}
        </div>
      </div>

      {/* Middle: top actors */}
      <div className="flex-1 min-w-[180px] border-b md:border-b-0 md:border-r border-[#E5E5E5] pb-4 md:pb-0 md:pr-6">
        <p
          className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold mb-3"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          Top movers
        </p>
        {(data.top_actors || []).length === 0 ? (
          <p className="text-xs text-neutral-400 italic">—</p>
        ) : (
          <ul className="space-y-2" data-testid="today-top-actors">
            {data.top_actors.map((a, i) => (
              <li key={a.email} className="flex items-center gap-2">
                <span
                  className="h-6 w-6 rounded-full bg-neutral-200 flex items-center justify-center text-[10px] font-black text-neutral-700"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  {i + 1}
                </span>
                <UserIcon size={12} weight="bold" className="text-neutral-500" />
                <span className="text-xs text-neutral-900 font-medium flex-1 truncate">{a.email}</span>
                <span
                  className="text-xs font-mono tabular-nums text-neutral-900 font-semibold"
                  data-testid={`today-actor-${i}-count`}
                >
                  {a.count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right: recent entries */}
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center justify-between mb-3">
          <p
            className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            Latest changes
          </p>
          <Link
            to="/activity"
            data-testid="today-view-all-link"
            className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-[#0A0A0A] flex items-center gap-1"
          >
            View all <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
        {(data.recent || []).length === 0 ? (
          <p className="text-xs text-neutral-400 italic">No recent activity.</p>
        ) : (
          <ul className="space-y-2" data-testid="today-recent-list">
            {data.recent.slice(0, 3).map((e, i) => {
              const meta = ACTION_META[e.action] || ACTION_META.update;
              const Icon = meta.icon;
              const label = e.task
                ? `${(e.task.module || "").toUpperCase()}${e.task.module ? " · " : ""}${e.task.object_id || e.task_id.slice(0, 6)}`
                : `deleted (${e.task_id.slice(0, 6)})`;
              return (
                <li
                  key={e.id}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`today-recent-${i}`}
                >
                  <span className={`h-5 w-5 rounded flex items-center justify-center border ${meta.color}`}>
                    <Icon size={10} weight="bold" />
                  </span>
                  <span className="font-mono text-[10px] tabular-nums bg-neutral-100 px-1.5 py-0.5 rounded">
                    {label}
                  </span>
                  <span className="text-neutral-600 truncate flex-1">
                    {e.actor_email}
                  </span>
                  <span className="text-neutral-400 shrink-0" title={new Date(e.timestamp).toLocaleString()}>
                    {timeAgo(e.timestamp)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
